import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stack,
  Title,
  Button,
  Group,
  Text,
  TextInput,
  Select,
  ActionIcon
} from '@mantine/core';
import {
  IconArrowLeft,
  IconDeviceFloppy
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { parentApi } from '@/features/parent/api/parentApi';
import { useAuthStore } from '@/features/auth/store/authStore';

export function MobileChildEditPage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const { data: children, isLoading } = useQuery({
    queryKey: ['children', user?.id],
    queryFn: () => parentApi.getChildren(),
    enabled: !!user?.id
  });

  const child = children?.find(c => c.id === childId);

  const [formData, setFormData] = useState({
    name: child?.name || '',
    grade: child?.grade?.toString() || ''
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // TODO: Implement save functionality
    // await parentApi.updateChild(childId, formData);

    setTimeout(() => {
      setIsSaving(false);
      navigate(`/mobile/parent/child/${childId}`);
    }, 500);
  };

  if (isLoading) {
    return (
      <Container size="sm" p="md">
        <Text size="sm" c="dimmed" align="center">
          Loading...
        </Text>
      </Container>
    );
  }

  if (!child) {
    return (
      <Container size="sm" p="md">
        <Stack gap="md" align="center" mt={40}>
          <Text size="sm" c="dimmed">
            Child not found
          </Text>
          <Button onClick={() => navigate('/mobile/parent/children')}>
            Back to Children
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="sm" p="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <ActionIcon onClick={() => navigate(-1)} variant="subtle" size="lg">
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>Edit Profile</Title>
          <div style={{ width: 40 }} />
        </Group>

        {/* Form */}
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Child's name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <Select
            label="Grade"
            placeholder="Select grade"
            value={formData.grade}
            onChange={(value) => setFormData({ ...formData, grade: value || '' })}
            data={[
              { value: '1', label: 'Grade 1' },
              { value: '2', label: 'Grade 2' },
              { value: '3', label: 'Grade 3' },
              { value: '4', label: 'Grade 4' },
              { value: '5', label: 'Grade 5' },
              { value: '6', label: 'Grade 6' },
              { value: '7', label: 'Grade 7' },
              { value: '8', label: 'Grade 8' },
              { value: '9', label: 'Grade 9' },
              { value: '10', label: 'Grade 10' },
              { value: '11', label: 'Grade 11' },
              { value: '12', label: 'Grade 12' }
            ]}
          />

          <Text size="xs" c="dimmed">
            Note: More profile fields coming soon. For now, please use the web version for complete profile management.
          </Text>

          {/* Actions */}
          <Group justify="space-between" mt="md">
            <Button
              variant="subtle"
              onClick={() => navigate(-1)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={isSaving}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Container>
  );
}
